import { useCallback, useEffect, useState } from 'react';
import { ClipboardList, CheckCircle2, Send, AlertCircle, Ban} from 'lucide-react';
import { StepRow } from '../../../components/admin/BiddingItemStatusUpdate/BiddingItemStatusUpdateEachUserRow';
import { BiddingItemStatusConst, CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst, statusOptions, type ItemStatusType } from '../../../constants';
import React from 'react';
import { UserRole } from '../../../types/UserRole';
import { type BiddingItemStatusUpdateRequest } from '../../../types/BiddingItemStatusUpdateRequest';
import { useAuth, type AuthenticatedUser } from '../../../contexts/AuthContext';
import { useBiddingItemStatusCounts, useBiddingItemStatusUpdateApply, useBiddingItemStatusUpdateConfirm } from '../../../hooks/useBiddingItemStatusUpdateQuery';
import { useMasters } from '../../../contexts/MastersContext';
import { useGetBiddingDateForApplyQuery } from '../../../hooks/useCalendarQuery';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

const BiddingItemStatusUpdateEachUserPage = () => {

  // ダイアログの状態管理用インターフェース
  interface DialogState {
    isOpen: boolean;
    title: string;
    message: string;
    action: (() => void) | null;  
  }

  const queryClient = useQueryClient();
  const { currentUser } = useAuth();  // 認証Context
  const { isMastersLoading, sellers } = useMasters();
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  
  const [isInitialized, setIsInitialized] = useState(false); //  初期化フラグ
  const isSeller = currentUser?.role === UserRole.Seller; //  売手判定
  const isJA = currentUser?.role === UserRole.JA;         //  JA判定
  const isFarmer = currentUser?.role === UserRole.Farmer; //生産者判定
  // アイコンのマッピング定義
  const STATUS_ICONS: Record<ItemStatusType, React.ReactNode> = {
    [BiddingItemStatusConst.INPUT]: <ClipboardList className="w-5 h-5 text-slate-500" />,
    [BiddingItemStatusConst.APPLIED]: <Send className="w-5 h-5 text-yellow-600" />,
    [BiddingItemStatusConst.CONFIRMED]: <CheckCircle2 className="w-5 h-5 text-green-600" />,
    [BiddingItemStatusConst.CANCEL]: <Ban className="w-5 h-5 text-red-600" />,
  };

  const getMasterCode = (user: AuthenticatedUser | null) : BiddingItemStatusUpdateRequest => {
    const request : BiddingItemStatusUpdateRequest = { sellerCode: 0, farmerCode: 0, biddingDate: Number(initialBiddingDate)};
    if(user !== null)
    {
      switch(user.role){
        case UserRole.Admin:
        case UserRole.JA:
            //市場、JAユーザー
            request.sellerCode = 0;
            request.farmerCode = 0;
          break;
        case UserRole.Seller:
            //売手ユーザー
            request.sellerCode = user.parentCode;
            request.farmerCode = 0;
          break;
        case UserRole.Farmer:
            //生産者ユーザー
            request.sellerCode = user.parentCode;
            request.farmerCode = user.childCode;
          break;
        default:
          //上記以外のユーザーの場合
          break;
      }
    }
    else {
      //nullの場合
    }
    return request;
  };

  // 検索条件
  const [activeFilters, setActiveFilters] = useState<BiddingItemStatusUpdateRequest & { listingCategory?: number, prefectureType?: number }>(() => getMasterCode(currentUser));

  // 確認ダイアログの状態
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    title: '',
    message: '',
    action: null,
  });

  // const { data: items, isLoading: isItemsLoading, error } = useBiddingItemStatusCounts(activeFilters, currentUser?.loginId ?? "");
  const { data: items, isFetching} = useBiddingItemStatusCounts(activeFilters, currentUser?.loginId ?? "");
  
  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate', 'all'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  const statusUpdateApply = useBiddingItemStatusUpdateApply();
  const statusUpdateConfirm = useBiddingItemStatusUpdateConfirm();

  const counts = React.useMemo(() => {
    const newCounts: Record<string, number> = {};
    statusOptions.forEach(opt => newCounts[opt.value] = 0);

    newCounts[BiddingItemStatusConst.INPUT] = items?.INPUT ?? 0;
    newCounts[BiddingItemStatusConst.APPLIED] = items?.APPLIED ?? 0;
    newCounts[BiddingItemStatusConst.CONFIRMED] = items?.CONFIRMED ?? 0;

    return newCounts;
  }, [items]);

  const displayFlowOptions = statusOptions.filter(
    (opt) => opt.value !== BiddingItemStatusConst.CANCEL && opt.value
  );

  // ▼ 初期値セットのEffect
  useEffect(() => {
    if (isDateLoading || isMastersLoading || !currentUser) return;
    if (isInitialized) return;

    let defaultPref: number = PrefTypeConst.INTERNAL;

    // 売手(isSeller) または 生産者(isFarmer) の場合、
    // 所属する売手のコード(parentCode)を使ってマスタから区分を取得する
    const targetSellerCode = currentUser?.parentCode;
    if ((isSeller || isFarmer) && targetSellerCode) {
      const myData = sellers.find(s => String(s.sellerCode) === String(targetSellerCode));
      if (myData) {
        defaultPref = myData.prefectureType;
      }
    }

    // 初期値をセット
    setActiveFilters(prev => ({
      ...prev,
      biddingDate: initialBiddingDate ? Number(initialBiddingDate) : Number(dayjs().tz().format("YYYYMMDD")),
      listingCategory: ListingCategoryConst.BIDDING,
      prefectureType: defaultPref,
    }));

    setIsInitialized(true);
    // 依存配列に isFarmer を追加しておくとより確実です
  }, [initialBiddingDate, isDateLoading, isMastersLoading, currentUser, isSeller, isFarmer, sellers, isInitialized]);

  // フィルター変更ハンドラー
  const handleBiddingDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value; 
    setActiveFilters(prev => ({
      ...prev,
      biddingDate: selectedDate ? Number(dayjs(selectedDate).format("YYYYMMDD")) : prev.biddingDate
    }));
  }, []);

    const handleCombinedCategoryChange = useCallback((selectedValue: string) => {

    const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
    
    setActiveFilters(prev => ({
      ...prev,
      listingCategory: listingCategory as any,
      prefectureType: prefCategory as any, 
    }));
  }, []);

  // ボタンクリック時：確認ダイアログを開く
  const initiateAction = (label: string, from: ItemStatusType, to: ItemStatusType) => {
    const amount = counts[from];
    setDialogState({
      isOpen: true,
      title: `${label}の確認`,
      message: `現在 ${amount} 件のデータがあります。\nすべてのデータを「${label}」して次のステータスへ移動しますか？`,
      action: () => handleStatusUpdate(to),
    });
  };

  const handleConfirm = () => {
    if (dialogState.action) dialogState.action();
    closeDialog();
  };

  const closeDialog = () => {
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  };

  // 権限チェックロジック
  // 現在のステータス（アクション元）とロールに基づいて実行可否を判定
  const canExecuteAction = (role?: UserRole, status?: ItemStatusType): boolean => {
    switch (status) {
      case BiddingItemStatusConst.INPUT: // 「未提出」の行にあるアクション（＝提出する）
        return role === UserRole.Admin || role === UserRole.Seller || role === UserRole.JA;
      case BiddingItemStatusConst.APPLIED: // 「提出」の行にあるアクション（＝確定する）
        return role === UserRole.Admin;
      default:
        return false;
    }
  };

  /**
   * ユーザーごとに表題を切替
   * @param role 
   * @returns 
   */
  const displayTitle = (role?: UserRole) => {
    switch(role) {
      case UserRole.Admin:
        return "出品情報の確定";
      case UserRole.JA:
      case UserRole.Seller:
        return "出品情報の提出";
      case UserRole.Farmer:
        return "出品情報の状態確認"; // TODO:タイトルはどうするのか
      default:
        return "";
    }
  };

  /**
   * ステータス更新処理
   *  */
  const handleStatusUpdate = useCallback((targetStatus: ItemStatusType) => {

    switch(targetStatus){
      case BiddingItemStatusConst.APPLIED: //提出にする
        return statusUpdateApply.mutate(
          { request: activeFilters },
          { onSuccess: () => {
            
          } }
        );
      case BiddingItemStatusConst.CONFIRMED: //確定にする
        return statusUpdateConfirm.mutate(
          { request: activeFilters},
          { onSuccess: () => {
            
          } }
        );
      default:
        return;
    }
  }, [statusUpdateApply, statusUpdateConfirm]);

  // ... (ローディング・エラー表示) ...
  // const isPageLoading = isItemsLoading;
  // if (isPageLoading) return <div className="p-10 text-center">Loading...</div>; // 簡易表示
  // if (error) return <div className="p-10 text-red-600">Error: {error.message}</div>;

  return (
    <div className="min-h-full bg-slate-50 flex place-self-start items-center justify-center p-4 font-sans text-slate-800">
      <div className="w-auto max-w-2xl bg-white shadow-lg border border-slate-200 overflow-hidden">
        
        {/* ヘッダー部分 */}
        <div className="p-4  flex items-center justify-between">
          <h1 className="text-3xl font-extrabold flex items-center">
            {/* 出品ステータス変更機能 */}
            {displayTitle(currentUser?.role)}
          </h1>
        </div>

        {/* メインコンテンツ */}
        <div className="p-4 flex flex-col items-center">

        {/* 次回入札日と出品区分と県内県外 */}
          <div className="w-full bg-white p-4 rounded-lg border border-slate-200 flex flex-wrap gap-6 items-end mb-6 shadow-sm">
            
            {/* 1. 入札日 (Datepicker) */}
            <div className="w-full sm:w-auto">
              <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">入札日</label>
              <input
                type="date"
                className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none min-w-0 safari-date-fix"
                value={
                  activeFilters.biddingDate 
                    ? dayjs(String(activeFilters.biddingDate).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD") 
                    : ""
                }
                onChange={handleBiddingDateChange}
              />
            </div>

            {/* 2. 出品区分（県内/県外セレクトボックス） */}
           <div className="w-full sm:w-auto">
            <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
            <select
              disabled={isSeller || isFarmer || isJA} 
              value={
                (activeFilters.listingCategory != null && activeFilters.prefectureType != null)
                  ? `${activeFilters.listingCategory}_${activeFilters.prefectureType}`
                  : ""
              }
              onChange={(e) => handleCombinedCategoryChange(e.target.value)}
              // 背景色の切り替え条件に (isSeller || isFarmer || isJA) を指定
              className={`w-full sm:w-56 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                (isSeller || isFarmer || isJA) ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white hover:bg-slate-50"
              }`}
            >
              {CombinedCategoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
            
          </div>

          {displayFlowOptions.map((option, index) => {
            const isLastStep = index === displayFlowOptions.length - 1;
            const nextOption = isLastStep ? null : displayFlowOptions[index + 1];
            
            // ボタンラベルの生成ロジック (次のステータスのラベル + "する")
            // 必要に応じてstatusOptionsにbuttonLabelプロパティを持たせる設計も可能です
            const buttonLabel = nextOption ? `${nextOption.label}する` : undefined;

            // 権限があるかどうかを判定
            const hasRole = canExecuteAction(currentUser?.role, option.value);

            return (
              <React.Fragment key={option.value}>
                <StepRow
                  label={option.label}
                  count={counts[option.value] || 0}
                  buttonLabel={buttonLabel}
                  icon={STATUS_ICONS[option.value]}
                  badgeColor={option.color}
                  isLastStep={isLastStep}
                  hasRole={hasRole}
                  onAction={() => {
                    if (nextOption) {
                      initiateAction(nextOption.label, option.value, nextOption.value);
                    }
                  }}
                />
              </React.Fragment>
            );
          })}

        </div>
      </div>

      {/* 確認ダイアログ (モーダル) */}
      {dialogState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          {/* 背景オーバーレイ */}
          <div 
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeDialog}
          ></div>
          
          {/* ダイアログ本体 */}
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden z-10 transform transition-all scale-100 animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4 text-slate-800">
                <div className="bg-blue-100 p-2 rounded-full">
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold">{dialogState.title}</h3>
              </div>
              
              <p className="text-slate-600 text-sm whitespace-pre-wrap mb-6 leading-relaxed">
                {dialogState.message}
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm active:scale-95 transition-all"
                >
                  全て実行する
                </button>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-2 border-t border-slate-100 flex justify-between items-center">
               <span className="text-xs text-slate-400">操作を確認してください</span>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={handleRefreshAll}
        disabled={isFetching}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default BiddingItemStatusUpdateEachUserPage;